import { chain, compact, map, get, find, capitalize, toLower, isEmpty } from "lodash"
import { NUMERIC_OPERATORS } from "helpers/filters/filter-ons"
import { compareId } from "helpers/securities"
import { FILTER_TYPES } from "helpers/filters/chips"
import { computeRawFilter as computeRawFilterGeneral } from "helpers/filters"

const EMPTY_ARRAY = []

export const hasInstrumentPortfolioUniverseFilter = (filter, alias) => chain(filter)
  .get("universe")
  .some(filter => {
    const { type, notIn } = filter
    // If the filter already contains the `not in portfolio` of the current refinstrument
    return type === "PORTFOLIO" && notIn === alias
  })
  .value()

export function getFactorCategoryBasedFilterIsDisabled(metricId, isHistoricalFactorSensitivityAvailable) {
  if (!isHistoricalFactorSensitivityAvailable && metricId === "historicalFactorSensitivity") {
    return true
  }
  return false
}

function mapFactorsToOptions(factors, id, metricType, currentFilter, isHistoricalFactorSensitivityAvailable) {
  return chain(factors)
    .pickBy(({ category }) => category === id)
    .map(({ label }, value) => ({
      label: `${label} ${metricType.label}`,
      type: metricType.id,
      dataType: metricType.dataType,
      operators: NUMERIC_OPERATORS,
      value,
      isUnsupported: getFactorCategoryBasedFilterIsDisabled(metricType.id, isHistoricalFactorSensitivityAvailable),
    }))
    .value()
}

export function computeFactorCategoryBasedFilters(categories, factors, currentFilter, isOpPro, isHistoricalFactorSensitivityAvailable) {
  const metricTypes = compact([{
    label: "Exposure",
    id: "factorExposure",
    dataType: "numeric",
  },
  !isOpPro && {
    label: "Predicted Factor Sensitivity",
    id: "predictedFactorSensitivity",
    dataType: "percent",
  },
  {
    label: "Historical Factor Sensitivity",
    id: "historicalFactorSensitivity",
    dataType: "percent",
  }])

  return chain(categories)
    .map(({ label, id }) => map(metricTypes, (metricType) => ({
      label: `${label} ${metricType.label}`,
      type: metricType.id,
      options: mapFactorsToOptions(factors, id, metricType, currentFilter, isHistoricalFactorSensitivityAvailable),
    })))
    .flatten()
    .value()
}

export const computeRawFilter = (editingFilter, editing, filterOns, securitiesDescriptors, portfolios, etfs, watchlists, swaps, indexes) => {
  if (!editing) {
    return null
  }

  if (editing.filterType === "universe") {
    const filter = get(editingFilter, [editing.filterType, editing.id])
    if (!filter) {
      return null
    }

    let universe

    switch (filter.type) {
      case "ETF":
        universe = find(etfs, { value: filter.in || filter.notIn })
        break
      case "WATCHLIST":
        universe = find(watchlists, { value: filter.in || filter.notIn })
        break
      case "SWAP":
        universe = find(swaps, { value: filter.in || filter.notIn })
        break
      case "INDEX":
        universe = find(indexes, { value: filter.in || filter.notIn })
        break
      default:
        universe = find(portfolios, { value: filter.in || filter.notIn })
        break
    }

    return {
      filterType: editing.filterType,
      filter: {
        operator: {
          label: filter.in ? "is in" : "is not in",
          value: filter.in ? "in" : "notIn",
        },
        filterOn: {
          label: filter.type === "ETF" ? "ETF" : capitalize(filter.type),
          value: toLower(filter.type),
        },
        values: universe ? [{ label: universe.label, value: filter.in || filter.notIn }] : null,
      },
    }
  } else if (editing.filterType === "securities") {
    const operator = {
      label: editing.id === "in" ? "is in" : "is not in",
      value: editing.id === "in" ? "in" : "notIn",
    }

    return {
      filterType: editing.filterType,
      filter: {
        operator,
        filterOn: {
          label: "Securities",
          value: "securities",
        },
        values: chain(editingFilter)
          .get(`securities.${operator.value}`)
          .map(elem => ({
            ...elem,
            country: chain(securitiesDescriptors)
              .find(security => compareId(security, elem))
              .get("country")
              .value(),
          }))
          .value(),
      },
    }
  } else {
    return computeRawFilterGeneral(editingFilter, editing, filterOns, securitiesDescriptors)
  }
}

export function computeAddedFilters(filter, filterOns, additionalParams = {}, portfolios, etfs, watchlists, swaps, indexes) {
  if (isEmpty(filter)) {
    return EMPTY_ARRAY
  }

  const addedFilters = []
  const filterKeys = Object.keys(filter)

  filterKeys.forEach((field, keyIndex) => {
    const filterType = FILTER_TYPES.find(ft => ft.field === field)

    if (filterType && filter[field] && filter[field].length !== 0) {
      const { fetcher } = filterType
      const filterMetadata = []
      const securitiesDescriptors = filterType.field === "securities" ? additionalParams.securitiesDescriptors : additionalParams.combinedFactorsMetaData

      fetcher(filterMetadata, {
        filter: filter[field],
        filterType,
        id: field,
        ...additionalParams,
        securitiesDescriptors,
      })

      filterMetadata.forEach(({ type, id }) => {
        const editing = { filterType: type, id }
        const computedFilter = computeRawFilter(filter, editing, filterOns, securitiesDescriptors, portfolios, etfs, watchlists, swaps, indexes)

        if (computedFilter) {
          addedFilters.push({
            id,
            type,
            filterOn: computedFilter.filter?.filterOn,
            operator: computedFilter.filter?.operator,
            values: computedFilter.filter?.values || [],
            isUnsupported: computedFilter.isUnsupported || false,
            isExisting: true,
            order: keyIndex,
          })
        }
      })
    }
  })

  return addedFilters.sort((a, b) => a.order - b.order)
}
